import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  FormHelperText,
  Grid,
  Step,
  StepButton,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import SavePreset from 'components/atoms/PresetSection/SavePreset';
import { every, findLastIndex, isEmpty, isFunction, map, size, slice, some } from 'lodash';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';

export interface ActionStepperProps {
  hideExecution?: boolean;
  validateStep?: () => Promise<boolean>;
  handleSubmit: () => void;
  setActiveStepForValidation: (step: number) => void;
  handleSaveAsPreset?: (name: string) => void;
  isStepFailed?: Record<number, boolean>;
  stepWarnings?: Record<number, string>;
  steps: {
    content: ReactNode;
    label: string;
    subLabel?: ReactNode;
    subDescription?: ReactNode;
    optional?: boolean;
    skip?: boolean;
    /**
     * Removes the step from view and from navigation while preserving its index
     * in the array, so callers can keep using fixed step indices. The stepper
     * never lands on a hidden step and auto-advances off one that becomes hidden.
     */
    hidden?: boolean;
    isLoading?: boolean;
    onNextOrBackClick?: () => void;
  }[];
  finishText?: string;
  nonLinear?: boolean;
  /**
   * While true, the stepper is not rendered at all and a loading indicator is
   * shown instead. Use this to avoid flashing steps before data needed to decide
   * the step list (e.g. whether prior jobs exist) has loaded.
   */
  isInitializing?: boolean;
  initializingText?: string;
  incompleteStepsMessage?: ReactNode | false;
  activeStateHandlers?: [activeStep: number, setActiveStep: React.Dispatch<React.SetStateAction<number>>];
  finishDisabled?: boolean;
  finishDisabledMessage?: ReactNode;
  preventAdvanceOnValidationFailure?: boolean;
}

export const ActionStepper: React.FC<React.PropsWithChildren<ActionStepperProps>> = ({
  hideExecution,
  validateStep,
  steps,
  handleSubmit,
  setActiveStepForValidation,
  handleSaveAsPreset,
  isStepFailed,
  stepWarnings,
  finishText = 'Execute',
  nonLinear = true,
  isInitializing = false,
  initializingText = 'Loading…',
  incompleteStepsMessage = 'There are uncompleted steps',
  activeStateHandlers,
  finishDisabled = false,
  finishDisabledMessage,
  preventAdvanceOnValidationFailure = false,
}) => {
  const [isAddingPreset, setIsAddingPreset] = useState(false);
  const activeStepState = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = activeStateHandlers || activeStepState;
  const allStepsValid = !some(isStepFailed) && !some(steps, 'isLoading');
  const hasValidateStepFunction = isFunction(validateStep);
  const isActiveStepLoading = Boolean(steps[activeStep]?.isLoading);
  const canFinish =
    !hideExecution &&
    (activeStep === size(steps) - 1 || every(slice(steps, activeStep + 1), (step) => step?.optional || step?.hidden));
  const stepsHiddenSignature = map(steps, (step) => (step?.hidden ? '1' : '0')).join('');
  const hiddenStepFlags = useMemo(() => stepsHiddenSignature.split(''), [stepsHiddenSignature]);
  const targetStepForHiddenActiveStep = useMemo(() => {
    if (stepsHiddenSignature[activeStep] !== '1') {
      return null;
    }

    const nextVisibleStep = hiddenStepFlags.findIndex((hiddenFlag, index) => index > activeStep && hiddenFlag !== '1');
    const previousVisibleStep = findLastIndex(
      hiddenStepFlags,
      (hiddenFlag, index) => index < activeStep && hiddenFlag !== '1'
    );
    const targetStep = nextVisibleStep !== -1 ? nextVisibleStep : previousVisibleStep;

    return targetStep !== -1 && targetStep !== activeStep ? targetStep : null;
  }, [activeStep, hiddenStepFlags, stepsHiddenSignature]);

  const updateActiveStep = (nextStep: number) => {
    const nextValidationStep = Math.min(nextStep, Math.max(size(steps) - 1, 0));

    if (setActiveStepForValidation !== setActiveStep) {
      setActiveStepForValidation(nextValidationStep);
    }
    setActiveStep(nextStep);
  };

  // A step can become hidden after it is already active (e.g. once we learn the
  // study has no prior jobs to load). Move off it to the nearest visible step so
  // the user is never stranded on an empty, hidden step.
  useEffect(() => {
    if (targetStepForHiddenActiveStep === null) {
      return;
    }

    updateActiveStep(targetStepForHiddenActiveStep);
  }, [targetStepForHiddenActiveStep]);

  const handleFinish = async () => {
    if (isActiveStepLoading || !allStepsValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      const isStepValid = hasValidateStepFunction ? await validateStep() : true;
      if (isStepValid) {
        await handleSubmit();
        if (activeStep === size(steps) - 1) {
          updateActiveStep(activeStep + nextStepCount());
        }
      }
      setIsSubmitting(false);
    } catch (error) {
      console.error(error);
      updateActiveStep(0);
      setIsSubmitting(false);
    }
  };

  const nextStepCount = () => {
    let nextStepsLength = 1;

    for (let i = activeStep + 1; i < size(steps); i++) {
      if (steps[i]?.skip || steps[i]?.hidden) {
        nextStepsLength++;
      } else {
        break;
      }
    }

    return nextStepsLength;
  };

  const handleNext = async () => {
    if (isActiveStepLoading) {
      return;
    }

    const isStepValid = hasValidateStepFunction ? await validateStep() : true;
    steps[activeStep]?.onNextOrBackClick?.();

    if (activeStep === size(steps) - 1) {
      if (isStepValid) {
        handleSubmit();
      }
    } else {
      if (isStepValid || !preventAdvanceOnValidationFailure) {
        updateActiveStep(activeStep + nextStepCount());
      }
    }
  };

  const backStepCount = () => {
    let nextStepsLength = 1;

    for (let i = activeStep - 1; i >= 0; i--) {
      if (steps[i]?.skip || steps[i]?.hidden) {
        nextStepsLength++;
      } else {
        break;
      }
    }

    return nextStepsLength;
  };

  const handleBack = () => {
    steps[activeStep]?.onNextOrBackClick?.();

    updateActiveStep(activeStep - backStepCount());
  };

  if (isInitializing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
        <CircularProgress size={20} />
        <Typography color="text.secondary">{initializingText}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stepper activeStep={activeStep} orientation="vertical" nonLinear={nonLinear}>
        {map(steps, (step, index) => {
          const showWarningCss = !isStepFailed?.[index] && stepWarnings?.[index];

          return (
            <Step key={step?.label} sx={step?.hidden ? { display: 'none' } : undefined}>
              <StepButton
                optional={step?.optional ? <Typography variant="caption">Optional</Typography> : null}
                onClick={async () => {
                  if (activeStep === index) {
                    return;
                  }

                  if (isActiveStepLoading) {
                    return;
                  }

                  steps[activeStep]?.onNextOrBackClick?.();
                  updateActiveStep(index);
                }}
              >
                <StepLabel
                  error={activeStep !== index && !step.isLoading && isStepFailed?.[index]}
                  StepIconProps={showWarningCss ? { sx: { color: 'warning.main' } } : {}}
                  sx={
                    showWarningCss
                      ? {
                          '.MuiStepLabel-label': {
                            color: 'warning.main',
                            fontWeight: 600,
                          },
                        }
                      : {}
                  }
                >
                  <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
                    <Grid item>
                      <Typography>
                        {step.label} {step.isLoading && <CircularProgress size={20} />}
                      </Typography>
                      {step.subLabel ? (
                        <Typography
                          variant="caption"
                          component="div"
                          sx={(theme) => ({
                            mt: 0.25,
                            color: activeStep === index ? 'text.secondary' : 'text.primary',
                            fontWeight:
                              activeStep === index
                                ? theme.typography.fontWeightRegular
                                : theme.typography.fontWeightMedium,
                            lineHeight: 1.35,
                          })}
                        >
                          {step.subLabel}
                        </Typography>
                      ) : null}
                      {step.subDescription ? (
                        <Typography
                          variant="caption"
                          component="div"
                          sx={(theme) => ({
                            mt: 0.25,
                            color: 'text.secondary',
                            fontWeight: theme.typography.fontWeightRegular,
                            lineHeight: 1.35,
                          })}
                        >
                          {step.subDescription}
                        </Typography>
                      ) : null}
                    </Grid>
                    {!isStepFailed?.[index] && !isEmpty(stepWarnings) && stepWarnings?.[index] && (
                      <Grid item>
                        <Alert variant="outlined" severity="warning" sx={{ border: 'none' }}>
                          {stepWarnings[index]}
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </StepLabel>
              </StepButton>
              <StepContent>
                {step.content}
                <Box sx={{ mb: 2 }}>
                  <Grid container wrap="nowrap" spacing={2} justifyContent="space-between">
                    <Grid container item alignItems="baseline">
                      {index !== size(steps) - 1 && (
                        <Button
                          variant="outlined"
                          onClick={handleNext}
                          disabled={isActiveStepLoading}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          Continue
                        </Button>
                      )}
                      <Button disabled={index === 0} onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
                        Back
                      </Button>
                    </Grid>
                    <Grid item container alignItems="start" justifyContent="end" mt={1} mr={1}>
                      {Boolean(handleSaveAsPreset) && (
                        <Grid item container xs={4}>
                          <Collapse in={!isAddingPreset}>
                            <Button
                              disabled={false}
                              onClick={() => {
                                setIsAddingPreset(true);
                              }}
                            >
                              Save as preset...
                            </Button>
                          </Collapse>
                          <SavePreset
                            isAddingPreset={isAddingPreset}
                            setIsAddingPreset={setIsAddingPreset}
                            isLoading={false}
                            savePreset={handleSaveAsPreset}
                          />
                        </Grid>
                      )}
                      {canFinish && (
                        <Grid item xs={2}>
                          <FormControl
                            fullWidth
                            variant="standard"
                            error={!allStepsValid}
                            className={!allStepsValid ? 'error' : undefined}
                          >
                            <Button
                              disabled={!allStepsValid || finishDisabled || isSubmitting || isActiveStepLoading}
                              variant="contained"
                              onClick={handleFinish}
                            >
                              {finishText}
                            </Button>
                            {finishDisabled && finishDisabledMessage && (
                              <FormHelperText>{finishDisabledMessage}</FormHelperText>
                            )}
                            {!allStepsValid && incompleteStepsMessage && (
                              <FormHelperText error id="class-does-not-exist-error-text">
                                {incompleteStepsMessage}
                              </FormHelperText>
                            )}
                            {isSubmitting && <FormHelperText>Submitting...</FormHelperText>}
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="flex-end" mt={1}>
                    {canFinish && (
                      <Grid item>
                        {!isEmpty(stepWarnings) && (
                          <Grid item xs={12} sx={{ mb: 1 }}>
                            {map(
                              stepWarnings,
                              (warning: string, stepIndex: number) =>
                                !isStepFailed?.[stepIndex] &&
                                !isEmpty(warning) && (
                                  <Alert key={stepIndex} severity="warning" sx={{ width: '100%' }}>
                                    {`Step ${Number(stepIndex) + 1}: ${warning}`}
                                  </Alert>
                                )
                            )}
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
      {activeStep === size(steps) && (
        <Grid container sx={{ mt: 1, ml: 1 }} spacing={1} data-testid="action-stepper-completion">
          <Grid item>
            <Button onClick={handleBack}>Back</Button>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
